// skipcq: JS-W1028, JS-0128
import React, { useCallback, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  USER_COHORT_CHART_SERIES,
  getUserCohortSeriesColor,
  getUserCohortSeriesItemSortKey,
} from '../../utils/userCohortDailyChart';

const CHART_HEIGHT_PX = 280;

function formatCohortTooltipValue(value, name) {
  return [Number(value).toLocaleString(), name];
}

function formatCohortTooltipLabel(label) {
  return typeof label === 'string' && label.length > 0 ? `Snapshot ${label}` : 'Snapshot';
}

/**
 * Daily registered-user cohort counts (userCohortReports rollups).
 */
function UserCohortTrendChart({ chartRowsAscending, loading, error }) {
  const theme = useTheme();
  const [hiddenSeriesKeys, setHiddenSeriesKeys] = useState(() => new Set(['inactive']));

  const handleLegendClick = useCallback((legendEntry) => {
    const dataKey = legendEntry?.dataKey;
    if (typeof dataKey !== 'string' || dataKey.length === 0) {
      return;
    }
    setHiddenSeriesKeys((previousKeys) => {
      const nextKeys = new Set(previousKeys);
      if (nextKeys.has(dataKey)) {
        nextKeys.delete(dataKey);
      } else {
        nextKeys.add(dataKey);
      }
      return nextKeys;
    });
  }, []);

  const formatLegendLabel = useCallback(
    (label, legendEntry) => {
      const dataKey = legendEntry?.dataKey;
      const isHidden =
        typeof dataKey === 'string' && hiddenSeriesKeys.has(dataKey);
      return (
        <span
          style={{
            color: isHidden ? theme.palette.text.disabled : theme.palette.text.primary,
            opacity: isHidden ? 0.55 : 1,
            cursor: 'pointer',
            textDecoration: isHidden ? 'line-through' : 'none',
          }}
        >
          {label}
        </span>
      );
    },
    [hiddenSeriesKeys, theme.palette.text.disabled, theme.palette.text.primary],
  );

  const legendPayload = useMemo(
    () =>
      USER_COHORT_CHART_SERIES.map((seriesConfig) => ({
        value: seriesConfig.label,
        type: 'square',
        color: getUserCohortSeriesColor(seriesConfig.dataKey),
        dataKey: seriesConfig.dataKey,
      })),
    [],
  );

  const tooltipContentStyle = useMemo(
    () => ({
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      color: theme.palette.text.primary,
    }),
    [theme],
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 2,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error">
        {error}
      </Typography>
    );
  }

  if (!Array.isArray(chartRowsAscending) || chartRowsAscending.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        No user cohort reports yet — run the scheduled reporter or backfill script.
      </Typography>
    );
  }

  const visibleSeriesCount = USER_COHORT_CHART_SERIES.length - hiddenSeriesKeys.size;

  return (
    <Box data-testid="user-cohort-trend-chart" sx={{ width: '100%' }}>
      <Box sx={{ width: '100%', height: CHART_HEIGHT_PX }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartRowsAscending}
            margin={{ top: 8, right: 12, left: 4, bottom: 4 }}
          >
            <XAxis dataKey="dateLabelShort" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 12 }} allowDecimals={false} width={48} />
            <Tooltip
              contentStyle={tooltipContentStyle}
              formatter={formatCohortTooltipValue}
              labelFormatter={formatCohortTooltipLabel}
              itemSorter={getUserCohortSeriesItemSortKey}
            />
            <Legend
              payload={legendPayload}
              wrapperStyle={{ fontSize: '0.85rem', cursor: 'pointer' }}
              onClick={handleLegendClick}
              formatter={formatLegendLabel}
              itemSorter={getUserCohortSeriesItemSortKey}
            />
            {USER_COHORT_CHART_SERIES.map((seriesConfig) => (
              <Bar
                key={seriesConfig.dataKey}
                hide={hiddenSeriesKeys.has(seriesConfig.dataKey)}
                name={seriesConfig.label}
                dataKey={seriesConfig.dataKey}
                fill={getUserCohortSeriesColor(seriesConfig.dataKey)}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
        Click legend labels to show or hide cohorts. The Y-axis rescales to visible series only.
      </Typography>
      {visibleSeriesCount === 0 ? (
        <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
          All cohorts are hidden — click a legend label to show at least one series.
        </Typography>
      ) : null}
    </Box>
  );
}

export default UserCohortTrendChart;
